import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2,
  Circle,
  Key,
  Workflow,
  X,
  Loader2,
  ArrowRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { apiClient } from '../api/client';
import { getIntegration } from '../lib/integrationCatalog';
import { IntegrationIcon } from './IntegrationIcon';

interface ChecklistItem {
  id: string;
  title: string;
  description: string;
  href: string;
  /** Lucide icon for non-integration items; integration items use integrationKey instead. */
  icon?: React.ComponentType<{ className?: string }>;
  /** Catalog key used to render the integration's brand icon. */
  integrationKey?: string;
  completed: boolean;
  loading: boolean;
}

// Shown when the tenant has no wizard selection (skipped it or predates it).
const DEFAULT_INTEGRATION_KEYS = ['kubernetes', 'aws', 'github', 'slack', 'pagerduty', 'confluence'];

function integrationChecklistItem(key: string): ChecklistItem | null {
  const def = getIntegration(key);
  if (!def) return null;
  return {
    id: `integration:${def.key}`,
    title: `Connect ${def.name}`,
    description: def.description,
    href: def.href,
    integrationKey: def.key,
    completed: false,
    loading: true,
  };
}

const CORE_ITEMS: ChecklistItem[] = [
  {
    id: 'workflow',
    title: 'Create your first workflow',
    description: 'Build an AI workflow to automatically respond to incidents and events.',
    href: '/workflows/new',
    icon: Workflow,
    completed: false,
    loading: true,
  },
  {
    id: 'apikey',
    title: 'Create an API key for CLI / MCP / SDK',
    description: 'Generate an API key to manage workflows programmatically via the Kestrel CLI, MCP server, or Python SDK.',
    href: '/api-keys',
    icon: Key,
    completed: false,
    loading: true,
  },
];

interface OnboardingChecklistProps {
  onVisibilityChange?: (visible: boolean) => void;
}

export function OnboardingChecklist({ onVisibilityChange }: OnboardingChecklistProps) {
  const [items, setItems] = useState<ChecklistItem[]>([]);
  const [dismissed, setDismissed] = useState(false);
  const [dismissedLoading, setDismissedLoading] = useState(true);

  useEffect(() => {
    // Load dismissed state from backend
    apiClient.getOnboardingChecklistStatus()
      .then(({ dismissed }) => {
        setDismissed(dismissed);
        setDismissedLoading(false);
      })
      .catch(() => {
        setDismissedLoading(false);
      });

    // Build the item list from the tenant's setup wizard selection, then
    // check statuses.
    let cancelled = false;
    (async () => {
      let keys = DEFAULT_INTEGRATION_KEYS;
      try {
        const state = await apiClient.getSetupWizardState();
        if (state.selected_integrations?.length > 0) {
          keys = state.selected_integrations;
        }
      } catch {
        // Fall back to defaults
      }
      if (cancelled) return;

      const integrationItems = keys
        .map(integrationChecklistItem)
        .filter((i): i is ChecklistItem => i !== null);
      const allItems = [...integrationItems, ...CORE_ITEMS.map((i) => ({ ...i }))];
      setItems(allItems);

      await Promise.allSettled([
        checkIntegrationStatuses(integrationItems.map((i) => i.id)),
        checkWorkflows(),
        checkApiKeys(),
      ]);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const updateItem = (id: string, completed: boolean) => {
    setItems(prev => prev.map(item =>
      item.id === id ? { ...item, completed, loading: false } : item
    ));
  };

  const checkIntegrationStatuses = async (itemIds: string[]) => {
    try {
      const statuses = await apiClient.getWorkflowIntegrationsStatus();
      const connected = new Set(
        (statuses || []).filter((s: any) => s.connected).map((s: any) => s.id as string)
      );
      for (const itemId of itemIds) {
        const key = itemId.replace(/^integration:/, '');
        const def = getIntegration(key);
        const statusId = def?.statusId || key;
        updateItem(itemId, connected.has(statusId));
      }
    } catch {
      for (const itemId of itemIds) {
        updateItem(itemId, false);
      }
    }
  };

  const checkWorkflows = async () => {
    try {
      const workflows = await apiClient.listWorkflows();
      updateItem('workflow', workflows.length > 0);
    } catch {
      updateItem('workflow', false);
    }
  };

  const checkApiKeys = async () => {
    try {
      const keys = await apiClient.listApiKeys();
      const hasActiveKey = (keys ?? []).some((k: any) => {
        if (k.revoked_at) return false;
        if (k.expires_at && new Date(k.expires_at) < new Date()) return false;
        return true;
      });
      updateItem('apikey', hasActiveKey);
    } catch {
      updateItem('apikey', false);
    }
  };

  const handleDismiss = async () => {
    try {
      await apiClient.dismissOnboardingChecklist();
    } catch {
      // Still dismiss locally even if the API call fails
    }
    setDismissed(true);
    onVisibilityChange?.(false);
  };

  const completedCount = items.filter(i => i.completed).length;
  const totalCount = items.length;
  const anyLoading = items.some(i => i.loading);
  const allLoading = items.every(i => i.loading);
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  const isHidden = dismissedLoading || dismissed || anyLoading || completedCount === totalCount;

  // Notify parent of visibility changes
  useEffect(() => {
    onVisibilityChange?.(!isHidden);
  }, [isHidden]);

  if (isHidden) {
    return null;
  }

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-3 px-4 pt-4">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-lg font-semibold">Get the most out of Kestrel</CardTitle>
          <div className="flex items-center gap-1 flex-shrink-0">
            <span className="text-xs font-semibold text-foreground">
              {allLoading ? '...' : `${progressPercent}%`}
            </span>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Hide onboarding checklist?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will remove the setup checklist from your dashboard. You won't be able to bring it back.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDismiss}>
                    Hide checklist
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0 px-2 pb-2 flex-1 flex flex-col">
        <div className="flex-1 flex flex-col justify-evenly">
          {items.map((item) => {
              return (
                <Link
                  key={item.id}
                  to={item.href}
                  className={`flex items-center gap-2.5 rounded-md px-2 py-2 transition-colors group ${
                    item.completed
                      ? 'opacity-60 hover:opacity-80'
                      : 'hover:bg-muted/50'
                  }`}
                >
                  {/* Status indicator */}
                  <div className="flex-shrink-0">
                    {item.loading ? (
                      <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />
                    ) : item.completed ? (
                      <CheckCircle2 className="h-4 w-4 text-foreground" />
                    ) : (
                      <Circle className="h-4 w-4 text-muted-foreground/40" />
                    )}
                  </div>

                  {/* Icon */}
                  <div className={`flex-shrink-0 rounded p-1 ${
                    item.completed
                      ? 'bg-muted text-muted-foreground'
                      : 'bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary'
                  }`}>
                    {item.integrationKey ? (
                      <IntegrationIcon integration={item.integrationKey} size={14} />
                    ) : item.icon ? (
                      <item.icon className="h-3.5 w-3.5" />
                    ) : null}
                  </div>

                  {/* Title */}
                  <p className={`flex-1 text-sm font-medium truncate ${
                    item.completed ? 'line-through text-muted-foreground' : 'text-foreground'
                  }`}>
                    {item.title}
                  </p>

                  {/* Action */}
                  {!item.completed && !item.loading && (
                    <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      <ArrowRight className="h-3.5 w-3.5 text-primary" />
                    </div>
                  )}
                </Link>
              );
            })}
        </div>
      </CardContent>
    </Card>
  );
}
